import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Calendar.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import WeeklyTimetableModal from "./WeeklyTimetableModal";
import DailyTimetable from "./DailyTimetable";
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  NO_RECORDS_MESSAGE,
  getDayRows,
  groupWeekByDay,
  toDateKey,
  toWeekKey,
} from "./timeTableUtils";

// Created once – a new localizer on every render forces the calendar to rebuild
const localizer = momentLocalizer(moment);
const CALENDAR_VIEWS = ["month"];

const getSession = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  return {
    school_id: clientData?.school_id || "",
    user_id: user?.id || "",
    classID: clientData?.class_id || "",
    sectionID: clientData?.student_section || "",
    staff_id: clientData?.master_id || "",
    isStaff: user?.role?.slug === "staff",
  };
};

const fetchTimeTable = async (session, date, isWeekly) => {
  const common = {
    school_id: session.school_id,
    weekend_id: moment(date).isoWeek(),
    shift_id: "",
    date_dt: isWeekly ? "" : toDateKey(date),
  };

  const [endpoint, payload] = session.isStaff
    ? [
        "timeTable/timeTableStaff",
        { ...common, staff_id: session.staff_id, class_id: "", section: "" },
      ]
    : [
        "timeTable/timeTableStudent",
        {
          ...common,
          student_id: parseInt(session.user_id),
          class_id: session.classID,
          section: session.sectionID,
        },
      ];

  const response = await AxiosInstance.post(endpoint, payload);

  if (response?.data?.status) {
    return { status: true, data: response.data.data || EMPTY_LIST, message: "" };
  }
  return {
    status: false,
    data: EMPTY_LIST,
    message: response?.data?.message || "",
  };
};

const MonthCalendar = memo(function MonthCalendar({ date, onSelectSlot }) {
  return (
    <Calendar
      localizer={localizer}
      date={date}
      view="month"
      views={CALENDAR_VIEWS}
      selectable
      onSelectSlot={onSelectSlot}
      toolbar={false}
      popup={false}
      drilldownView={null}
      startAccessor="start"
      endAccessor="end"
      className={styles.calendar}
    />
  );
});

const StaffTimeTable = () => {
  // Read the user cookie once per mount instead of on every render
  const session = useMemo(getSession, []);
  const { isStaff } = session;

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [daily, setDaily] = useState({ data: EMPTY_LIST, message: "" });
  // Resolved weeks by week key: { [weekKey]: { key, byDay } }
  const [weeks, setWeeks] = useState(EMPTY_OBJECT);

  // One promise per date/week: re-clicks, reopening the modal and the daily
  // fallback all reuse the same response instead of calling the API again.
  const [cache] = useState(() => new Map());
  const latestDailyKey = useRef(null);

  const cached = useCallback(
    (cacheKey, factory) => {
      if (!cache.has(cacheKey)) {
        const request = factory().catch((err) => {
          // Don't cache failures so the next attempt can retry
          cache.delete(cacheKey);
          throw err;
        });
        cache.set(cacheKey, request);
      }
      return cache.get(cacheKey);
    },
    [cache],
  );

  const loadWeek = useCallback(
    (date) => {
      const weekKey = toWeekKey(date);
      return cached(`week:${weekKey}`, async () => {
        const storeWeek = (byDay) => {
          const week = { key: weekKey, byDay };
          setWeeks((prev) => ({ ...prev, [weekKey]: week }));
          return week;
        };
        try {
          const result = await fetchTimeTable(session, date, true);
          return storeWeek(
            result.status ? groupWeekByDay(result.data, date) : EMPTY_OBJECT,
          );
        } catch (err) {
          // Stop the modal showing "Loading..." – the failed request isn't cached, so reopening retries
          storeWeek(EMPTY_OBJECT);
          throw err;
        }
      });
    },
    [cached, session],
  );

  const loadDaily = useCallback(
    (date) => {
      const dateKey = toDateKey(date);
      latestDailyKey.current = dateKey;

      cached(`day:${dateKey}`, async () => {
        const result = await fetchTimeTable(session, date, false);

        if (result.status) {
          // Sort once here instead of on every render
          return {
            data: [...result.data].sort((a, b) => a.period - b.period),
            message: "",
          };
        }
        if (result.message !== NO_RECORDS_MESSAGE) {
          // Holiday (or other) message – show the card without rows
          return { data: EMPTY_LIST, message: result.message };
        }

        // No rows for this exact date: use this weekday's periods from the weekly
        // timetable (cached, so opening the weekly modal later costs no request)
        const week = await loadWeek(date);
        return { data: getDayRows(week.byDay, date), message: "" };
      })
        .then((result) => {
          // Ignore responses for a date the user has already moved away from
          if (latestDailyKey.current === dateKey) setDaily(result);
        })
        .catch((err) => console.error(err));
    },
    [cached, loadWeek, session],
  );

  useEffect(() => {
    loadDaily(selectedDate);
    // Initial load only – later loads are triggered by date clicks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateClick = useCallback(
    (slotInfo) => {
      const clickedDate = slotInfo.start;
      setSelectedDate((prev) =>
        moment(prev).isSame(clickedDate, "day") ? prev : clickedDate,
      );
      loadDaily(clickedDate);
    },
    [loadDaily],
  );

  const handleOpen = () => {
    setOpen(true);
    loadWeek(selectedDate).catch((err) => console.error(err));
  };

  const handleClose = useCallback(() => setOpen(false), []);

  const weekKey = useMemo(() => toWeekKey(selectedDate), [selectedDate]);
  // undefined while the selected week is still loading
  const weekByDay = weeks[weekKey]?.byDay;

  return (
    <div className={styles.container}>
      {/* LEFT – Calendar */}
      <div className={styles.calendarWrapper}>
        {/* Calendar Toolbar */}
        <div className={styles.header}>
          {/* Left controls */}
          <div className={styles.left}>
            {/* Previous Month */}
            <button
              onClick={() =>
                setSelectedDate((d) => moment(d).subtract(1, "month").toDate())
              }
              className={styles.iconBtn}
            >
              &lt;
            </button>

            {/* Next Month */}
            <button
              onClick={() =>
                setSelectedDate((d) => moment(d).add(1, "month").toDate())
              }
              className={styles.iconBtn}
            >
              &gt;
            </button>

            {/* Today */}
            <button
              onClick={() => setSelectedDate(new Date())}
              className={styles.todayBtn}
            >
              today
            </button>
          </div>

          {/* Month text */}
          <div className={styles.monthText}>
            {" "}
            {moment(selectedDate).format("MMMM YYYY").toUpperCase()}
          </div>
          <button className={styles.monthBtn}>month</button>
        </div>

        {/* Calendar Table */}
        <div className={styles.calendarPanel}>
          <MonthCalendar date={selectedDate} onSelectSlot={handleDateClick} />
        </div>
      </div>

      {/* RIGHT – Timetable */}
      <div className={styles.timetableWrapper}>
        <div className={styles.topBar}>
          <button className={styles.pill}>
            {" "}
            {moment(selectedDate).format("DD MMMM YYYY").toUpperCase()}
          </button>
          <button className={styles.pill} onClick={handleOpen}>
            Weekly Timetable
          </button>
        </div>
        {daily.message && daily.message !== NO_RECORDS_MESSAGE && (
          <div className={styles.card}>
            <h1 className={styles.title}>Local Holiday</h1>
            <p className={styles.subtitle}>{daily.message}</p>
          </div>
        )}

        {open && (
          <WeeklyTimetableModal
            date={selectedDate}
            byDay={weekByDay}
            onClose={handleClose}
          />
        )}

        <DailyTimetable data={daily.data} date={selectedDate} isStaff={isStaff} />
      </div>
    </div>
  );
};

export default StaffTimeTable;
